import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/msalConfig';
import { authService } from '../services/auth.service';
import Cookies from 'universal-cookie';
import Swal from 'sweetalert2';
import { jwtDecode } from 'jwt-decode';

const cookies = new Cookies();

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [user, setUser] = useState(authService.currentUserValue);
    const [loading, setLoading] = useState(true);
    const [initialCheckComplete, setInitialCheckComplete] = useState(false);
    const { instance, accounts } = useMsal();

    useEffect(() => {
        const subscription = authService.currentUser.subscribe(user => {
            setUser(user);
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    const isValidReturnUrl = (url) => {
        if (!url || typeof url !== 'string') return false;
        if (!url.startsWith('/') || url.startsWith('//')) return false;
        if (url.length > 500) return false;
        const dangerousChars = /[<>"'`\x00-\x1f\x7f-\x9f]/;
        if (dangerousChars.test(url)) return false;

        try {
            const baseUrl = window.location.origin;
            new URL(url, baseUrl);
            return true;
        } catch {
            return false;
        }
    };

    const handlePostLoginRedirect = useCallback((user) => {
        const storedReturnUrl = localStorage.getItem('msal_return_url');
        if (storedReturnUrl && isValidReturnUrl(storedReturnUrl)) {
            localStorage.removeItem('msal_return_url');
            window.location.href = storedReturnUrl;
            return;
        }

        if (user.roles.some(role => role.role === 'ADMIN')) {
            window.location.href = '/';
        } else if (user.roles.some(role => role.role === 'TECNOLOGIA')) {
            window.location.href = '/';
        } else {
            window.location.href = '/';
        }
    }, []);

    const isTokenExpired = (tokenObj) => {
        if (!tokenObj || !tokenObj.exp) {
            return true;
        }
        const currentTime = Math.floor(Date.now() / 1000);
        return tokenObj.exp < currentTime;
    };

    useEffect(() => {
        const checkAuthState = async () => {
            try {
                await instance.initialize();

                const authToken = cookies.get('authToken');
                if (authToken) {
                    try {
                        const decodedToken = jwtDecode(authToken);

                        if (isTokenExpired(decodedToken)) {
                            cookies.remove('authToken', { path: '/' });
                            cookies.remove('impersonateToken', { path: '/' });
                        } else {
                            const impToken = cookies.get('impersonateToken');

                            if (impToken) {
                                const decodedImpToken = jwtDecode(impToken);
                                authService.validateToken(impToken, decodedImpToken, true);
                            } else {
                                authService.validateToken(authToken, decodedToken);
                            }

                            setLoading(false);
                            setInitialCheckComplete(true);
                            return;
                        }
                    } catch (error) {
                        console.error('AuthContext: JWT token decoding error:', error);
                        cookies.remove('authToken', { path: '/' });
                        cookies.remove('impersonateToken', { path: '/' });
                    }
                }

                const redirectResponse = await instance.handleRedirectPromise();

                if (redirectResponse && redirectResponse.account && redirectResponse.accessToken) {

                    if (!authService.currentUserValue || !authService.currentUserValue.usuario) {
                        try {
                            Swal.fire({
                                title: 'Autenticando...',
                                text: 'Procesando tu información de Microsoft',
                                allowOutsideClick: false,
                                showConfirmButton: false,
                                didOpen: () => {
                                    Swal.showLoading();
                                }
                            });

                            const result = await authService.loginWithMsal(redirectResponse.accessToken);
                            if (result.success) {
                                localStorage.removeItem('app_logged_out');

                                Swal.fire({
                                    icon: 'success',
                                    title: '¡Autenticación exitosa!',
                                    text: 'Bienvenido',
                                    timer: 1500,
                                    showConfirmButton: false
                                });

                                return;
                            } else {
                                Swal.fire({
                                    icon: 'error',
                                    title: 'Error de autenticación',
                                    text: result.error || 'No se pudo completar la autenticación',
                                    confirmButtonText: 'Intentar de nuevo'
                                });
                            }
                        } catch (apiError) {
                            console.error('Error processing MSAL redirect response:', apiError);
                            Swal.fire({
                                icon: 'error',
                                title: 'Error inesperado',
                                text: 'Ocurrió un error durante la autenticación',
                                confirmButtonText: 'Aceptar'
                            });
                        }
                    }

                }

                const hasLoggedOut = localStorage.getItem('app_logged_out') === 'true';

                if (!hasLoggedOut) {
                    if ((!authService.currentUserValue || !authService.currentUserValue.usuario) && accounts.length > 0) {
                        const account = accounts[0];
                        try {
                            const tokenResponse = await instance.acquireTokenSilent({
                                ...loginRequest,
                                account: account
                            });

                            if (tokenResponse.accessToken) {
                                const loadingTimeout = setTimeout(() => {
                                    Swal.fire({
                                        title: 'Restaurando sesión...',
                                        text: 'Un momento por favor',
                                        allowOutsideClick: false,
                                        showConfirmButton: false,
                                        didOpen: () => {
                                            Swal.showLoading();
                                        }
                                    });
                                }, 1000);

                                const result = await authService.loginWithMsal(tokenResponse.accessToken);

                                clearTimeout(loadingTimeout);
                                Swal.close();

                                if (result.success) {
                                    localStorage.removeItem('app_logged_out');
                                }
                            }
                        } catch (msalError) {
                            console.log('MSAL token refresh failed:', msalError);
                        }
                    }
                }
            } catch (error) {
                console.error('Error checking auth state:', error);
                setUser(null);
                authService.currentUserSubject.next(null);
            } finally {
                setLoading(false);
                setInitialCheckComplete(true);
            }
        };

        checkAuthState();
    }, [accounts, instance, handlePostLoginRedirect]);

    const loginWithMsal = async () => {
        try {
            Swal.fire({
                title: 'Redirigiendo...',
                text: 'Te estamos llevando a Microsoft para autenticarte',
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            await instance.initialize();
            await instance.loginRedirect(loginRequest);
            return { success: true };
        } catch (error) {
            console.error('MSAL login redirect error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error al iniciar sesión',
                text: 'No se pudo redirigir a Microsoft para la autenticación',
                confirmButtonText: 'Aceptar'
            });
            return { success: false };
        }
    };

    const logout = async () => {
        await instance.initialize();
        await authService.logoutWithMsal(instance, accounts);
    };

    const value = {
        user,
        loginWithMsal,
        logout,
        loading,
        initialCheckComplete,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}